import React, { useEffect, useMemo, useState } from "react";
import {
    Activity,
    HeartPulse,
    Volume2,
    Loader2,
    Pill,
    CheckCircle2,
    Clock,
    TrendingUp,
    LineChart as LineChartIcon,
    X,
} from "lucide-react";
import { useClara } from "../contexts/ClaraContext";
import { useData } from "../contexts/DataContext";
import { MEDICATIONS, TODAY_SUMMARY } from "../data/sampleData";
import HistoryChart from "../components/HistoryChart";

const SIMULATED_BP = { systolic: 122, diastolic: 78, pulse: 68 };
const DAY_MS = 24 * 60 * 60 * 1000;

const formatTime = (ts) =>
    new Date(ts).toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "2-digit",
    });

const formatDayLabel = (ts) =>
    new Date(ts).toLocaleDateString("en-US", {
        weekday: "long",
        month: "short",
        day: "numeric",
    });

export const HealthVitals = () => {
    const { speak } = useClara();
    const { glucoseHistory, bpHistory, latestGlucose, addBp } = useData();
    const [bpState, setBpState] = useState("idle"); // idle | measuring | done
    const [bpReading, setBpReading] = useState(null);
    const [showHistory, setShowHistory] = useState(false);
    const [drillDay, setDrillDay] = useState(null); // ts of day-start

    const glucose24hAvg =
        glucoseHistory && glucoseHistory.length
            ? Math.round(
                  glucoseHistory
                      .slice(-4)
                      .reduce((s, g) => s + g.value, 0) /
                      Math.min(4, glucoseHistory.length),
              )
            : 121;

    const dayReadings = useMemo(() => {
        if (!drillDay) return [];
        const end = drillDay + DAY_MS;
        return glucoseHistory
            .filter((g) => g.ts >= drillDay && g.ts < end)
            .sort((a, b) => a.ts - b.ts);
    }, [drillDay, glucoseHistory]);

    const dayStats = useMemo(() => {
        if (!dayReadings.length)
            return { avg: 0, min: 0, max: 0, inRange: 0, pct: 0 };
        const vals = dayReadings.map((g) => g.value);
        const avg = Math.round(vals.reduce((s, v) => s + v, 0) / vals.length);
        const min = Math.min(...vals);
        const max = Math.max(...vals);
        const inRange = vals.filter((v) => v >= 80 && v <= 140).length;
        const pct = Math.round((inRange / vals.length) * 100);
        return { avg, min, max, inRange, pct };
    }, [dayReadings]);

    useEffect(() => {
        speak(
            "Health Vitals. Your glucose is in range. You can take a new blood pressure reading from here.",
        );
    }, [speak]);

    const readGlucoseAloud = () => {
        speak(
            `Your current glucose reading is ${latestGlucose.value} milligrams per deciliter. That's in your healthy range.`,
        );
    };

    const takeNewBP = () => {
        if (bpState === "measuring") return;
        speak(
            "I'm connecting to your blood pressure monitor. Put the cuff on your upper arm and try to stay relaxed.",
        );
        setBpState("measuring");
        setBpReading(null);
        setTimeout(() => {
            // Save to history (small jitter so the chart visibly updates)
            const jitter = Math.floor(Math.random() * 5) - 2;
            const reading = {
                systolic: SIMULATED_BP.systolic + jitter,
                diastolic: SIMULATED_BP.diastolic + (jitter > 0 ? 1 : 0),
                pulse: SIMULATED_BP.pulse + jitter,
            };
            setBpReading(reading);
            setBpState("done");
            addBp(reading);
            speak(
                `Your blood pressure is ${reading.systolic} over ${reading.diastolic}. Pulse ${reading.pulse}. This is in the healthy range. I've saved it to your health journal.`,
            );
        }, 3500);
    };

    const toggleHistory = () => {
        const next = !showHistory;
        setShowHistory(next);
        if (!next) setDrillDay(null);
        speak(
            next
                ? "Showing your seven day history. Tap any point on the glucose chart to see all readings for that day."
                : "Hiding the history charts.",
        );
    };

    const handleGlucosePoint = (point) => {
        if (!point || !point.ts) return;
        const date = new Date(point.ts);
        date.setHours(0, 0, 0, 0);
        const dayStart = date.getTime();
        setDrillDay(dayStart);
        const readings = glucoseHistory.filter(
            (g) => g.ts >= dayStart && g.ts < dayStart + DAY_MS,
        );
        if (readings.length) {
            const vals = readings.map((r) => r.value);
            const avg = Math.round(vals.reduce((s, v) => s + v, 0) / vals.length);
            speak(
                `${formatDayLabel(dayStart)}. ${readings.length} readings, averaging ${avg} milligrams per deciliter.`,
            );
        }
    };

    return (
        <div className="space-y-5">
            {/* Glucose */}
            <section
                aria-label="Continuous Glucose Monitor"
                data-testid="cgm-card"
                className="bg-white rounded-3xl p-6 border border-[#0A3D7A]/10 shadow-[0_4px_20px_rgba(10,61,122,0.06)]"
            >
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <span
                            aria-hidden="true"
                            className="w-10 h-10 rounded-xl bg-[#E5F1EB] flex items-center justify-center"
                        >
                            <Activity className="w-5 h-5 text-[#0A5C36]" />
                        </span>
                        <div>
                            <p className="text-xs uppercase tracking-wider font-semibold text-[#5A5A6E]">
                                Continuous Glucose
                            </p>
                            <p className="font-display font-bold text-lg text-[#1A1A2E]">
                                Monitor
                            </p>
                        </div>
                    </div>
                    <span className="inline-flex items-center gap-1 text-xs font-bold uppercase tracking-wider rounded-full px-3 py-1.5 bg-[#E5F1EB] text-[#0A5C36]">
                        <CheckCircle2 className="w-3.5 h-3.5" aria-hidden="true" />
                        In Range
                    </span>
                </div>

                <div className="mt-6 flex items-end gap-3">
                    <span
                        className="font-display font-black text-[#0A5C36] leading-none"
                        style={{ fontSize: "5rem" }}
                        aria-label={`${latestGlucose.value} milligrams per deciliter`}
                        data-testid="glucose-reading"
                    >
                        {latestGlucose.value}
                    </span>
                    <span className="pb-2 text-[#5A5A6E] font-semibold">
                        mg/dL
                    </span>
                </div>

                <div className="mt-2 flex items-center gap-1.5 text-sm text-[#0A5C36] font-semibold">
                    <TrendingUp className="w-4 h-4" aria-hidden="true" />
                    Trending slightly up · stable
                </div>

                <button
                    type="button"
                    onClick={readGlucoseAloud}
                    data-testid="read-glucose-aloud-btn"
                    className="mt-5 w-full min-h-[56px] rounded-2xl bg-[#0A3D7A] hover:bg-[#082F5E] text-white text-lg font-bold flex items-center justify-center gap-2 transition-colors shadow-sm"
                >
                    <Volume2 className="w-5 h-5" aria-hidden="true" />
                    Read My Reading Aloud
                </button>

                <dl className="mt-5 grid grid-cols-2 gap-3 text-sm">
                    <div className="rounded-2xl bg-[#FFF8F0] border border-[#F0EAD6] p-3">
                        <dt className="text-xs uppercase tracking-wider font-semibold text-[#5A5A6E]">
                            24-hour avg
                        </dt>
                        <dd className="font-display font-bold text-2xl text-[#1A1A2E] mt-0.5">
                            {glucose24hAvg}{" "}
                            <span className="text-sm font-medium text-[#5A5A6E]">
                                mg/dL
                            </span>
                        </dd>
                    </div>
                    <div className="rounded-2xl bg-[#FFF8F0] border border-[#F0EAD6] p-3">
                        <dt className="text-xs uppercase tracking-wider font-semibold text-[#5A5A6E]">
                            Last sync
                        </dt>
                        <dd className="font-display font-bold text-2xl text-[#1A1A2E] mt-0.5">
                            2 min ago
                        </dd>
                    </div>
                </dl>

                <p className="mt-4 text-xs text-[#5A5A6E] leading-relaxed">
                    Compatible with Dexcom G7, Freestyle Libre 3, Medtronic.
                    <span className="block italic mt-0.5">
                        Demo data — device not actually connected.
                    </span>
                </p>
            </section>

            {/* BP */}
            <section
                aria-label="Blood Pressure Monitor"
                data-testid="bp-card"
                className="bg-white rounded-3xl p-6 border border-[#0A3D7A]/10 shadow-[0_4px_20px_rgba(10,61,122,0.06)]"
            >
                <div className="flex items-center gap-2">
                    <span
                        aria-hidden="true"
                        className="w-10 h-10 rounded-xl bg-[#E8EEF7] flex items-center justify-center"
                    >
                        <HeartPulse className="w-5 h-5 text-[#0A3D7A]" />
                    </span>
                    <div>
                        <p className="text-xs uppercase tracking-wider font-semibold text-[#5A5A6E]">
                            Blood Pressure
                        </p>
                        <p className="font-display font-bold text-lg text-[#1A1A2E]">
                            Monitor
                        </p>
                    </div>
                </div>

                {bpState !== "measuring" && bpReading && (
                    <div
                        data-testid="bp-reading"
                        className="mt-5 rounded-2xl bg-[#F4F8FF] border border-[#0A3D7A]/15 p-5 text-center fade-in-up"
                    >
                        <p className="font-display font-black text-5xl text-[#0A3D7A] leading-none">
                            {bpReading.systolic}/{bpReading.diastolic}
                        </p>
                        <p className="mt-1 text-sm text-[#5A5A6E] font-semibold">
                            mmHg · Pulse {bpReading.pulse}
                        </p>
                        <p className="mt-3 inline-flex items-center gap-1.5 rounded-full px-3 py-1 bg-[#E5F1EB] text-[#0A5C36] text-xs font-bold uppercase tracking-wider">
                            <CheckCircle2
                                className="w-3.5 h-3.5"
                                aria-hidden="true"
                            />
                            Healthy range
                        </p>
                    </div>
                )}

                {bpState === "measuring" && (
                    <div
                        className="mt-5 rounded-2xl bg-[#0A3D7A] text-white p-5 flex items-center gap-3 fade-in-up"
                        data-testid="bp-measuring"
                    >
                        <Loader2
                            className="w-6 h-6 animate-spin shrink-0"
                            aria-hidden="true"
                        />
                        <div>
                            <p className="font-bold text-lg">Measuring…</p>
                            <p className="text-sm text-white/90">
                                Hold still. Cuff is inflating.
                            </p>
                        </div>
                    </div>
                )}

                <button
                    type="button"
                    onClick={takeNewBP}
                    disabled={bpState === "measuring"}
                    data-testid="take-new-bp-btn"
                    className="mt-5 w-full min-h-[56px] rounded-2xl bg-[#0A3D7A] hover:bg-[#082F5E] text-white text-lg font-bold flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed transition-colors shadow-sm"
                >
                    <HeartPulse className="w-5 h-5" aria-hidden="true" />
                    {bpState === "measuring"
                        ? "Measuring…"
                        : bpReading
                          ? "Take Another Reading"
                          : "Take New Reading"}
                </button>

                <p className="mt-4 text-xs text-[#5A5A6E] leading-relaxed">
                    Compatible with Omron, A&amp;D Medical, Withings, Qardio.
                    <span className="block italic mt-0.5">
                        Demo data — device not actually connected.
                    </span>
                </p>
            </section>

            {/* History toggle + charts */}
            <button
                type="button"
                onClick={toggleHistory}
                data-testid="toggle-history-btn"
                aria-expanded={showHistory}
                aria-label={
                    showHistory
                        ? "Hide seven day history charts"
                        : "Show seven day history charts"
                }
                className="w-full min-h-[56px] rounded-2xl bg-white border-2 border-[#0A3D7A] text-[#0A3D7A] text-base font-bold flex items-center justify-center gap-2 hover:bg-[#0A3D7A]/5 transition-colors"
            >
                <LineChartIcon className="w-5 h-5" aria-hidden="true" />
                {showHistory ? "Hide 7-day history" : "Show 7-day history"}
            </button>

            {showHistory && (
                <section
                    data-testid="history-charts"
                    className="bg-white rounded-3xl p-6 border border-[#0A3D7A]/10 shadow-[0_4px_20px_rgba(10,61,122,0.06)] fade-in-up space-y-5"
                >
                    <div>
                        <div className="flex items-center justify-between mb-2">
                            <p className="text-xs uppercase tracking-wider font-semibold text-[#5A5A6E]">
                                Glucose · 7-day trend
                            </p>
                            <span className="text-[11px] text-[#B8860B] font-semibold">
                                Tap a point for details
                            </span>
                        </div>
                        <HistoryChart
                            history={glucoseHistory}
                            metric="glucose"
                            height={160}
                            testId="glucose-history-chart"
                            onPointClick={handleGlucosePoint}
                        />
                    </div>

                    {drillDay && (
                        <div
                            data-testid="glucose-drilldown"
                            className="rounded-2xl bg-[#FFF8F0] border border-[#F0EAD6] p-4 fade-in-up"
                        >
                            <div className="flex items-center justify-between mb-3">
                                <div>
                                    <p className="text-[11px] uppercase tracking-wider font-semibold text-[#B8860B]">
                                        Drill-down
                                    </p>
                                    <p
                                        className="font-display font-bold text-lg text-[#1A1A2E] leading-tight"
                                        data-testid="drilldown-day-label"
                                    >
                                        {formatDayLabel(drillDay)}
                                    </p>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => setDrillDay(null)}
                                    aria-label="Close glucose drill-down"
                                    data-testid="drilldown-close-btn"
                                    className="w-9 h-9 rounded-lg text-[#5A5A6E] hover:bg-white flex items-center justify-center"
                                >
                                    <X className="w-4 h-4" />
                                </button>
                            </div>
                            <div className="grid grid-cols-3 gap-2 mb-3">
                                <DrillStat
                                    label="Avg"
                                    value={dayStats.avg}
                                    unit="mg/dL"
                                />
                                <DrillStat
                                    label="Lowest"
                                    value={dayStats.min}
                                    unit="mg/dL"
                                />
                                <DrillStat
                                    label="Highest"
                                    value={dayStats.max}
                                    unit="mg/dL"
                                />
                            </div>
                            <ul
                                className="space-y-2"
                                data-testid="drilldown-readings"
                            >
                                {dayReadings.map((r) => {
                                    const inRange =
                                        r.value >= 80 && r.value <= 140;
                                    return (
                                        <li
                                            key={r.ts}
                                            className="flex items-center justify-between gap-2 rounded-xl bg-white border border-[#F0EAD6] p-3"
                                        >
                                            <span className="text-sm text-[#5A5A6E] font-semibold">
                                                {formatTime(r.ts)}
                                            </span>
                                            <span className="flex-1 mx-3 text-base font-bold text-[#1A1A2E]">
                                                {r.value}{" "}
                                                <span className="text-xs font-medium text-[#5A5A6E]">
                                                    mg/dL
                                                </span>
                                            </span>
                                            <span
                                                className={`text-[10px] font-bold uppercase tracking-wider rounded-full px-2.5 py-0.5 ${
                                                    inRange
                                                        ? "bg-[#E5F1EB] text-[#0A5C36]"
                                                        : r.value < 80
                                                          ? "bg-[#FFE6E8] text-[#A31621]"
                                                          : "bg-[#FFF1D6] text-[#7A5A0A]"
                                                }`}
                                            >
                                                {inRange
                                                    ? "In range"
                                                    : r.value < 80
                                                      ? "Low"
                                                      : "High"}
                                            </span>
                                        </li>
                                    );
                                })}
                            </ul>
                            <p className="text-xs text-[#5A5A6E] mt-3 leading-snug">
                                {dayStats.inRange} of {dayReadings.length}{" "}
                                readings ({dayStats.pct}%) were in your healthy
                                range.
                            </p>
                        </div>
                    )}

                    <div>
                        <p className="text-xs uppercase tracking-wider font-semibold text-[#5A5A6E] mb-2">
                            Blood pressure · 7-day trend
                        </p>
                        <HistoryChart
                            history={bpHistory}
                            metric="bp"
                            height={160}
                            testId="bp-history-chart"
                        />
                    </div>
                </section>
            )}

            {/* Medications */}
            <section
                aria-label="Medications"
                data-testid="medications-card"
                className="bg-white rounded-3xl p-6 border border-[#0A3D7A]/10 shadow-[0_4px_20px_rgba(10,61,122,0.06)]"
            >
                <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                        <span
                            aria-hidden="true"
                            className="w-10 h-10 rounded-xl bg-[#F0EAD6] flex items-center justify-center"
                        >
                            <Pill className="w-5 h-5 text-[#7A5A0A]" />
                        </span>
                        <div>
                            <p className="text-xs uppercase tracking-wider font-semibold text-[#5A5A6E]">
                                Today's
                            </p>
                            <p className="font-display font-bold text-lg text-[#1A1A2E]">
                                Medications
                            </p>
                        </div>
                    </div>
                </div>
                <ul className="space-y-3">
                    {MEDICATIONS.map((m) => {
                        const taken = m.status === "taken";
                        return (
                            <li
                                key={m.id}
                                data-testid={`med-${m.id}`}
                                className="flex items-center gap-3 p-3 rounded-2xl bg-[#FFF8F0] border border-[#F0EAD6]"
                            >
                                <span
                                    aria-hidden="true"
                                    className={`shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${
                                        taken
                                            ? "bg-[#0A5C36] text-white"
                                            : "bg-[#0A3D7A] text-white"
                                    }`}
                                >
                                    {taken ? (
                                        <CheckCircle2 className="w-5 h-5" />
                                    ) : (
                                        <Clock className="w-5 h-5" />
                                    )}
                                </span>
                                <div className="flex-1 min-w-0">
                                    <p className="font-semibold text-[#1A1A2E]">
                                        {m.name}{" "}
                                        <span className="font-normal text-[#5A5A6E]">
                                            · {m.dose}
                                        </span>
                                    </p>
                                    <p className="text-sm text-[#5A5A6E]">
                                        {taken
                                            ? `Taken at ${m.time}`
                                            : `Due at ${m.time}`}
                                    </p>
                                </div>
                                <span
                                    className={`text-xs font-bold uppercase tracking-wider rounded-full px-3 py-1 ${
                                        taken
                                            ? "bg-[#E5F1EB] text-[#0A5C36]"
                                            : "bg-[#FFF1D6] text-[#7A5A0A]"
                                    }`}
                                >
                                    {taken ? "Taken" : "Pending"}
                                </span>
                            </li>
                        );
                    })}
                </ul>
            </section>
        </div>
    );
};

export default HealthVitals;

const DrillStat = ({ label, value, unit }) => (
    <div className="rounded-xl bg-white border border-[#F0EAD6] p-2 text-center">
        <p className="text-[10px] uppercase tracking-wider font-semibold text-[#5A5A6E]">
            {label}
        </p>
        <p className="font-display font-bold text-xl text-[#1A1A2E] leading-none mt-0.5">
            {value}
        </p>
        <p className="text-[10px] text-[#5A5A6E] mt-0.5">{unit}</p>
    </div>
);
